import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  LinearProgress,
  Divider,
  Tooltip,
  CircularProgress,
  Chip,
  useTheme,
} from '@mui/material';
import {
  AccountBalanceWallet,
  Inventory2,
  CheckCircle,
  WarningAmber,
  ErrorOutline,
} from '@mui/icons-material';
import { formatIndianNumber } from '../../utils/currency';
import { api } from '../../services/apiClient';

// ---------------------------------------------------------------------------
// Credit control + Available-to-Promise panel for the Sales Order 360.
// Both cards render ONLY real figures from the backend:
//   GET /sales/customers/:id/credit-status?amount=<order total>
//   GET /sales/orders/:id/availability
// Nothing here is hardcoded; if the API errors, the card says so instead of
// showing fake numbers.
// ---------------------------------------------------------------------------

interface CreditStatus {
  creditLimit: number;
  creditDays: number;
  outstanding: number;
  overdue: number;
  openOrderValue: number;
  currentOrder: number;
  projectedExposure: number;
  availableCredit: number;
  utilizationPct: number | null;
  status: 'NO_LIMIT_SET' | 'WITHIN_LIMIT' | 'EXCEEDED';
  withinLimit: boolean;
}

interface AtpLine {
  productId: number;
  productCode?: string;
  productName?: string;
  isStockItem: boolean;
  required: number;
  onHand: number;
  committed: number;
  availableToPromise: number;
  incoming: number;
  shortfall: number;
  status: 'AVAILABLE' | 'SHORTFALL' | 'NON_STOCK';
}

interface Availability {
  salesOrderId: number;
  canFulfil: boolean;
  incomingNote?: string;
  lines: AtpLine[];
}

const Row: React.FC<{ label: string; value: React.ReactNode; strong?: boolean; tone?: string }> = ({
  label,
  value,
  strong,
  tone,
}) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.75 }}>
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body2" sx={{ fontWeight: strong ? 700 : 600, color: tone }}>
      {value}
    </Typography>
  </Box>
);

const CreditAvailabilityPanel: React.FC<{
  salesOrderId: number | string;
  customerId?: number | string;
  orderAmount?: number;
}> = ({ salesOrderId, customerId, orderAmount = 0 }) => {
  const theme = useTheme();
  const [credit, setCredit] = useState<CreditStatus | null>(null);
  const [creditErr, setCreditErr] = useState<string | null>(null);
  const [atp, setAtp] = useState<Availability | null>(null);
  const [atpErr, setAtpErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      setLoading(true);
      const jobs: Promise<void>[] = [];
      if (customerId) {
        jobs.push(
          api
            .get(`/sales/customers/${customerId}/credit-status?amount=${encodeURIComponent(orderAmount)}`)
            .then((res: any) => {
              if (alive) setCredit(res?.credit ?? res ?? null);
            })
            .catch((e: any) => {
              if (alive) setCreditErr(e?.response?.data?.message || 'Could not load credit status');
            }),
        );
      }
      jobs.push(
        api
          .get(`/sales/orders/${salesOrderId}/availability`)
          .then((res: any) => {
            if (alive) setAtp(res?.availability ?? res ?? null);
          })
          .catch((e: any) => {
            if (alive) setAtpErr(e?.response?.data?.message || 'Could not load stock availability');
          }),
      );
      await Promise.all(jobs);
      if (alive) setLoading(false);
    };
    load();
    return () => {
      alive = false;
    };
  }, [salesOrderId, customerId, orderAmount]);

  const creditTone =
    credit?.status === 'EXCEEDED'
      ? theme.palette.error.main
      : credit?.status === 'NO_LIMIT_SET'
      ? theme.palette.text.secondary
      : theme.palette.success.main;

  const util = credit?.utilizationPct ?? 0;
  const utilBarColor =
    util >= 100 ? theme.palette.error.main : util >= 80 ? theme.palette.warning.main : theme.palette.success.main;

  const panelSx = {
    p: 2.5,
    borderRadius: 3,
    border: '1px solid',
    borderColor: 'divider',
    height: '100%',
  } as const;

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2.5, mb: 2.5 }}>
      {/* Credit control */}
      <Paper elevation={0} sx={panelSx}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
          <AccountBalanceWallet fontSize="small" color="action" />
          <Typography variant="subtitle2" sx={{ fontWeight: 700, flex: 1 }}>
            Credit Control
          </Typography>
          {credit && (
            <Chip
              size="small"
              label={
                credit.status === 'EXCEEDED'
                  ? 'Limit Exceeded'
                  : credit.status === 'NO_LIMIT_SET'
                  ? 'No Limit Set'
                  : 'Within Limit'
              }
              icon={
                credit.status === 'EXCEEDED' ? (
                  <ErrorOutline sx={{ fontSize: 16 }} />
                ) : credit.status === 'NO_LIMIT_SET' ? undefined : (
                  <CheckCircle sx={{ fontSize: 16 }} />
                )
              }
              sx={{
                fontWeight: 700,
                color: creditTone,
                bgcolor: `${creditTone}1f`,
                '& .MuiChip-icon': { color: creditTone },
              }}
            />
          )}
        </Box>

        {loading && !credit && !creditErr ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={22} />
          </Box>
        ) : creditErr ? (
          <Typography variant="body2" color="text.secondary">
            {creditErr}
          </Typography>
        ) : credit ? (
          <>
            <Row label="Credit Limit" value={credit.creditLimit > 0 ? formatIndianNumber(credit.creditLimit) : '—'} />
            <Row label="Outstanding" value={formatIndianNumber(credit.outstanding)} />
            {credit.overdue > 0 && (
              <Row label="of which Overdue" value={formatIndianNumber(credit.overdue)} tone={theme.palette.error.main} />
            )}
            <Row label="Open Orders" value={formatIndianNumber(credit.openOrderValue)} />
            <Row label="This Order" value={formatIndianNumber(credit.currentOrder)} />
            <Divider sx={{ my: 1 }} />
            <Row label="Projected Exposure" value={formatIndianNumber(credit.projectedExposure)} strong />
            <Row
              label="Available Credit"
              value={credit.creditLimit > 0 ? formatIndianNumber(credit.availableCredit) : '—'}
              strong
              tone={credit.availableCredit < 0 ? theme.palette.error.main : theme.palette.success.main}
            />
            {credit.utilizationPct != null && (
              <Box sx={{ mt: 1.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">
                    Utilisation
                  </Typography>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: utilBarColor }}>
                    {credit.utilizationPct.toFixed(0)}%
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={Math.min(util, 100)}
                  sx={{
                    height: 7,
                    borderRadius: 4,
                    bgcolor: `${utilBarColor}22`,
                    '& .MuiLinearProgress-bar': { backgroundColor: utilBarColor, borderRadius: 4 },
                  }}
                />
              </Box>
            )}
          </>
        ) : (
          <Typography variant="body2" color="text.secondary">
            No customer on this order.
          </Typography>
        )}
      </Paper>

      {/* Availability / ATP */}
      <Paper elevation={0} sx={panelSx}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
          <Inventory2 fontSize="small" color="action" />
          <Typography variant="subtitle2" sx={{ fontWeight: 700, flex: 1 }}>
            Stock Availability (ATP)
          </Typography>
          {atp && (
            <Chip
              size="small"
              label={atp.canFulfil ? 'Can Fulfil' : 'Shortfall'}
              icon={
                atp.canFulfil ? <CheckCircle sx={{ fontSize: 16 }} /> : <WarningAmber sx={{ fontSize: 16 }} />
              }
              sx={{
                fontWeight: 700,
                color: atp.canFulfil ? theme.palette.success.main : theme.palette.warning.main,
                bgcolor: `${atp.canFulfil ? theme.palette.success.main : theme.palette.warning.main}1f`,
                '& .MuiChip-icon': {
                  color: atp.canFulfil ? theme.palette.success.main : theme.palette.warning.main,
                },
              }}
            />
          )}
        </Box>

        {loading && !atp && !atpErr ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={22} />
          </Box>
        ) : atpErr ? (
          <Typography variant="body2" color="text.secondary">
            {atpErr}
          </Typography>
        ) : atp && atp.lines.length > 0 ? (
          <>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '1fr auto auto auto',
                gap: 0.5,
                px: 0.5,
                mb: 0.5,
              }}
            >
              {['Product', 'Req', 'ATP', 'Short'].map((h, i) => (
                <Typography
                  key={h}
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontWeight: 700, textAlign: i === 0 ? 'left' : 'right' }}
                >
                  {h}
                </Typography>
              ))}
            </Box>
            {atp.lines.map((l) => {
              const tone =
                l.status === 'SHORTFALL'
                  ? theme.palette.warning.main
                  : l.status === 'NON_STOCK'
                  ? theme.palette.text.secondary
                  : theme.palette.success.main;
              return (
                <Box
                  key={l.productId}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '1fr auto auto auto',
                    gap: 0.5,
                    px: 0.5,
                    py: 0.6,
                    borderTop: '1px solid',
                    borderColor: 'divider',
                    alignItems: 'center',
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {l.productName || l.productCode || `#${l.productId}`}
                    </Typography>
                    {l.status === 'NON_STOCK' && (
                      <Typography variant="caption" color="text.secondary">
                        Non-stock
                      </Typography>
                    )}
                  </Box>
                  <Typography variant="body2" sx={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                    {l.required}
                  </Typography>
                  <Tooltip title={`On hand ${l.onHand} − committed ${l.committed}`}>
                    <Typography variant="body2" sx={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                      {l.status === 'NON_STOCK' ? '—' : l.availableToPromise}
                    </Typography>
                  </Tooltip>
                  <Typography
                    variant="body2"
                    sx={{ textAlign: 'right', fontWeight: 700, color: tone, fontVariantNumeric: 'tabular-nums' }}
                  >
                    {l.status === 'NON_STOCK' ? '—' : l.shortfall > 0 ? l.shortfall : '0'}
                  </Typography>
                </Box>
              );
            })}
            {atp.incomingNote && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                {atp.incomingNote}
              </Typography>
            )}
          </>
        ) : (
          <Typography variant="body2" color="text.secondary">
            No line items to check.
          </Typography>
        )}
      </Paper>
    </Box>
  );
};

export default CreditAvailabilityPanel;
